import React, { useState, useCallback, useEffect } from 'react';
import { Button, Modal, Input } from 'antd';
import { CustomCollapseCard, CustomTable, Text, useText } from '@src/components';
import { useDispatch, useSelector } from 'react-redux';
import {
  incrementActiveStep,
  decrementActiveStep,
} from '@src/store/slice/addNewCarStore/addCarTypeSlice';
import {
  postVehicles,
  resetCurrentLoadAddCarPlateDetail,
} from '@src/store/slice/addNewCarStore/addCarPlateDetailSlice';
import '@src/styles/addNewCar/addCarOrderSummary.scss';
import CustomIconComponent from '@src/components/CustomIconComponent';
import { useMediaQuery } from '@src/hooks/useMediaQuery';
import CustomAddCarOrderSummaryFleetLimit from '@src/components/CustomAddCarOrderSummaryFleetLimit';
import customersServices from '@src/services/customers.services';
import { moneyFormat } from '@src/utils/utils';
import { getVehicleList } from '@src/store/slice/vehicleManagementStore/vehicleManagementSlice';
import { useHistory } from 'react-router-dom';
import ErrorDialog from '@src/components/ErrorDialog';

const { confirm } = Modal;
const AddNewCarOrderSummary = () => {
  const dispatch = useDispatch();
  const isMobil = useMediaQuery({ query: '(max-width: 768px)' });
  const {
    allPlatesDetailItems,
    vehicleTypes = [],
    vehicleAttributes = [],
    vehicleFuelTypes = [],
  } = useSelector((state) => state.addCarPlateDetail);
  const { demandNo } = useSelector((state) => state.addCarType);
  const { isAdmin } = useSelector((state) => state.auth);
  const { selectedUserId, currentUser } = useSelector((state) => state.user);
  const [loading, setLoading] = useState(false);
  const [columns, setColumns] = useState([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isFleetModalVisible, setIsFleetModalVisible] = useState(false);
  const [cusVouList, setCusVouList] = useState([]);
  const { selectedCustomer } = useSelector((state) => state.customer);
  const [value, setValue] = useState('');
  const [errorValue, setErrorValue] = useState(false);
  const { vehicleLimit } = useSelector((state) => state.vehicleManagement);
  const history = useHistory();
  const { allPlatesArray, allPlatesDetails } = useSelector(
    (state) => state?.addCarPlateInformation,
  );

  const wouldYouLikeToIncreaseYourFleetLimit = useText('wouldYouLikeToIncreaseYourFleetLimit');
  const pleaseEnterTheLimitAmountYouWantTo = useText('pleaseEnterTheLimitAmountYouWantTo');
  
  useEffect(async () => {
    fetchVehicleListData();
    const vouList = await customersServices.customerVouList({
      customerId: selectedCustomer?.customerId,
      vouId: 4,
    });
    if (vouList?.data) {
      setCusVouList(vouList.data);
    }
  }, [dispatch, selectedCustomer]);

  const fetchVehicleListData = async () => {
    if (isAdmin && selectedUserId) {
      await dispatch(getVehicleList({ customerId: selectedUserId }));
    } else {
      await dispatch(getVehicleList());
    }
  };

  /**
   * The wizard stores the label that was on screen when the option was picked, so on a language
   * switch the summary would keep showing the previous language. Resolve the label from the
   * currently loaded lookup list by its id instead, and only fall back to the stored one.
   */
  const resolveLabel = (list, idKey, labelKey, id, fallback) =>
    list?.find((item) => item?.[idKey] === id)?.[labelKey] ?? fallback;

  useEffect(() => {
    let tempColumns = [
      {
        title: '#',
        dataIndex: 'id',
        key: 'id',
        fixed: 'left',
        align: 'center',
        width: 40,
        height: 50,
      },
      {
        title: <Text t="licensePlate" />,
        dataIndex: 'plateNumber',
        key: 'plateNumber',
        align: 'left',
        width: 90,
        height: 50,
      },
      {
        title: <Text t="limit" />,
        dataIndex: 'limitValue',
        key: 'limitValue',
        align: 'left',
        width: 70,
        height: 50,
        render: (_, record) => {
          if (record.limitDetails?.limitValue) {
            return (
              record.limitDetails?.limitValue +
              (record.limitDetails?.limitValueType?.name === 'Tutar' ? ' ₺' : ' lt')
            );
          }
          return '';
        },
      },
    ];
    if (!isMobil) {
      tempColumns = tempColumns.concat([
        {
          title: <Text t="provider2" />,
          dataIndex: 'vouId',
          key: 'vouId',
          align: 'left',
          width: 70,
          height: 50,
          render: (_, record) => {
            const vouId = record.selectCustomerVousList?.vouId;
            const IconComponent = CustomIconComponent({ providerType: vouId });

            return (
              <div style={{ display: 'flex', alignItems: 'center' }}>
                {IconComponent && (
                  <img
                    src={IconComponent}
                    alt={record.selectCustomerVousList?.vouName}
                    style={{ width: 24, height: 24, marginRight: 8 }}
                  />
                )}
              </div>
            );
          },
        },
        {
          title: <Text t="vehicleType2" />,
          dataIndex: 'name',
          key: 'name',
          align: 'left',
          width: 80,
          height: 50,
          render: (_, record) =>
            resolveLabel(
              vehicleTypes,
              'vehicleTypeId',
              'vehicleTypeName',
              record.vehicleDetails?.vehicleTypeId,
              record.vehicleDetails?.vehicleType?.name,
            ),
        },
        {
          title: <Text t="vehicleQualification2" />,
          dataIndex: 'name',
          key: 'name',
          align: 'left',
          width: 90,
          height: 50,
          render: (_, record) =>
            resolveLabel(
              vehicleAttributes,
              'vehicleAttributeId',
              'vehicleAttributeName',
              record.vehicleDetails?.attributeNameId,
              record.vehicleDetails?.attribute?.name,
            ),
        },
        {
          title: <Text t="fuelType" />,
          dataIndex: 'name',
          key: 'name',
          align: 'left',
          width: 90,
          height: 50,
          render: (_, record) =>
            resolveLabel(
              vehicleFuelTypes,
              'fuelTypeId',
              'fuelTypeLabel',
              record.vehicleDetails?.fuelTypeId,
              record.vehicleDetails?.vehicleFuelType?.name,
            ),
        },
        {
          title: <Text t="driverName2" />,
          dataIndex: 'driverName',
          key: 'driverName',
          align: 'left',
          width: 100,
          height: 50,
          render: (_, record) => record.driverDetails?.driverName,
        },
        {
          title: <Text t="driverPhone" />,
          dataIndex: 'driverPhone',
          key: 'driverPhone',
          align: 'left',
          width: 120,
          height: 50,
          render: (_, record) => {
            const phone = record.driverDetails?.driverPhone;
            const formattedPhone = phone
              ? phone.replace(/(\d{3})(\d{3})(\d{2})(\d{2})/, '0($1) $2 $3 $4')
              : '';
            return formattedPhone;
          },
        },
      ]);
    }
    setColumns(tempColumns);
  }, [isMobil, vehicleTypes, vehicleAttributes, vehicleFuelTypes]);

  const formatPhoneNumber = (phone) => {
    return phone.replace(/[^\d]/g, '').replace(/^90/, '');
  };

  const createVehicleRequest = (plate) => {
    return {
      plate: getPlate(plate),
      providerType: getProviderType(plate),
      externalId: getExternalId(plate),
      ...getLimitDetails(plate),
      ...getVehicleDetails(plate),
      ...getDriverDetails(plate),
      ...getContactDetails(plate),
      externalInfoJson: 'string',
    };
  };

  const getPlate = (plate) => plate.plateNumber || '';
  const getProviderType = (plate) => plate.selectVehicleDeviceTypeList?.vouId || 0;
  const getExternalId = (plate) => null; //String(plate.id) || '0';

  const getLimitDetails = (plate) => ({
    limitType: plate.limitDetails?.limitTypeId || 0,
    limitValueType: plate.limitDetails?.limitValueTypeId || 1,
    limitAmount: plate.limitDetails?.limitValue || 0,
    usableDays: plate.limitDetails?.fuelDays || null,
  });

  const getVehicleDetails = (plate) => ({
    vehicleType: plate.vehicleDetails?.vehicleTypeId || 0,
    vehicleUsageType: plate.vehicleDetails?.attributeNameId || 0,
    deliveryAddressId: plate.vehicleDetails?.addressGetListId || 0,
    deviceTypeId: plate.selectVehicleDeviceTypeList?.deviceTypeId || 0,
    departmentId: plate.vehicleDetails?.departmanId || 0,
    costCenterId: plate.vehicleDetails?.costCenterId || 0,
    campaignId: 0,
  });

  const getDriverDetails = (plate) => ({
    driverNameSurname: plate.driverDetails?.driverName || '',
    driverGsm: formatPhoneNumber(plate.driverDetails?.driverPhone || ''),
    driverEmail: plate.driverDetails?.driverEmail || '',
    isSendMailToDriver: plate.driverDetails?.sendDriverCode || false,
    isSendMailToApprover: plate.driverDetails?.sendOrderCode || false,
  });

  const getContactDetails = (plate) => ({
    customerId: plate.selectCustomerVousList?.customerId || 0,
    segmentId: plate.vehicleDetails?.fuelTypeId || 0,
  });

  const prepareVehiclesRequest = (plates) => {
    return {
      vehicles: plates?.map(createVehicleRequest),
    };
  };

  const handleCarBackStep = useCallback(async () => {
    if (allPlatesDetails && allPlatesArray) {
      dispatch(decrementActiveStep());
    } else {
      confirm({
        title: <Text t="areYouSureYouWantToGoBack" />,
        content: <Text t="ifYouPerformThisTransactionYourCurrentInformation2" />,
        okText: <Text t="yes" />,
        cancelText: <Text t="no" />,
        centered: true,
        onOk() {
          dispatch(resetCurrentLoadAddCarPlateDetail());
          dispatch(decrementActiveStep());
        },
        onCancel() { },
      });
    }
  }, []);

  const handleCarApproval = useCallback(async () => {
    const requestPostVehicles = prepareVehiclesRequest(allPlatesDetailItems);
    try {
      if (isAdmin && demandNo && demandNo !== 0 && requestPostVehicles) {
        requestPostVehicles.vehicles?.map(
          (item) => (item.salesforceTicketNo = parseInt(demandNo, 10)),
        );
      }
      if (isAdmin && selectedUserId && requestPostVehicles) {
        /*try {
          requestPostVehicles.UserName = currentUser?.userName;
          requestPostVehicles.ActiveCustomerId = selectedUserId;
        } catch (errorx) {
          console.error('error', errorx);
        }*/
        requestPostVehicles.vehicles?.map((item) => (item.customerId = selectedUserId));
      }
      requestPostVehicles?.vehicles?.map((item) => {
        if (item.usableDays === null) {
          item.usableDays = '1111111';
        }
      });
    } catch (error) {
      console.error('error', error);
    }
    setLoading(true);

    try {
      const response = await dispatch(postVehicles(requestPostVehicles)).unwrap();
      if (response.data === false) {
        console.error('Error Response', response);
        ErrorDialog({
          title: <Text t="error" />,
        });
      } else if (response.isSuccess) {
        if (
          cusVouList?.filter(
            (item) =>
              (isAdmin ? selectedUserId : selectedCustomer?.customerId ?? '').toString() ===
              item.customerId.toString() &&
              item.patId === 2 &&
              item.pamId === 4,
          )?.length > 0
        ) {
          setIsModalVisible(true);
        } else {
          increment();
        }
      }
    } catch (error) {
      console.error('Error posting vehicles:', error);
    } finally {
      setLoading(false);
    }
  }, [allPlatesDetailItems, dispatch, cusVouList]);

  const increment = async () => {
    await dispatch(incrementActiveStep());
  };

  const handleCancel = () => {
    setIsModalVisible(false);
    increment();
  };

  const handleOk = () => {
    setIsModalVisible(false);
    setIsFleetModalVisible(true);
  };

  const handleFleetCancel = () => {
    setIsFleetModalVisible(false);
    increment();
  };

  const confirmAddFleetLimit = () => {
    setIsFleetModalVisible(false);
    if (isAdmin) {
      history.push('/admin/dashboard');
    } else {
      history.push('/ticket-car/add-fleet-limit', { addNewCarValue: value, allPlatesDetailItems });
    }
    increment();
  };

  const handleChange = (e) => {
    const inputValue = e.target.value;
    if (/^(\d+([.,]\d*)?)?$/.test(inputValue)) {
      setValue(inputValue);
      if (errorValue) setErrorValue(false);
    }
  };

  return (
    <div data-testid="add-new-car-order-summary-component">
      <CustomCollapseCard
        className="add-new-car-order-summary"
        cardTitle={<Text t="orderSummary" />}
      >
        <div className="balance-report-header-tools">
          <Text t="newlyAddedVehicleList" />
        </div>
        <CustomTable
          pagination={false}
          dataSource={allPlatesDetailItems}
          columns={columns}
          className="babs-list-table"
          rowKey={(record) => `add-new-car-order-summary-list-${record?.id}`}
        />
      </CustomCollapseCard>
      <div className="step-btn-container" style={{ marginTop: '30px' }}>
        <Button
          id="add-new-car-container-draft-btn"
          className="draft-btn"
          onClick={handleCarBackStep}
        >
          <Text t="turnBackBtn" />
        </Button>
        <Button
          id="add-new-car-container-next-btn"
          className="next-btn"
          disabled={allPlatesDetailItems.length === 0}
          onClick={handleCarApproval}
          loading={loading}
        >
          <Text t="approve2" />
        </Button>

        <CustomAddCarOrderSummaryFleetLimit
          title={wouldYouLikeToIncreaseYourFleetLimit}
          isModalVisible={isModalVisible}
          handleCancel={handleCancel}
          clickOk={handleOk}
        />
        <Modal
          visible={isFleetModalVisible}
          onCancel={handleFleetCancel}
          className="add-new-car-order-summary-modal"
          style={{
            borderRadius: '8px', // Modal köşelerini yuvarlama
            padding: '12px 14px 40px 39px',
          }}
          footer={null}
          centered
          maskClosable={false}
          keyboard={false}
        >
          <div
            className="monthly-order-container"
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexDirection: 'column',
            }}
          >
            <span className="filo-limit-add-modal-title">
              <Text t="setYourNewFleetLimit" />
            </span>
            <span className="filo-limit-add-modal-sub-title">
              <Text t="yourCurrentFleetLimit" /> {vehicleLimit ? moneyFormat(vehicleLimit) : 0}TL
            </span>

            <div
              style={{
                //backgroundColor: '#f2f5f7',
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <span style={{ marginTop: 20, marginRight: 220 }}>
                <Text t="newLimitAmount" />
              </span>

              <Input
                data-testid={`fleet-limit-input`}
                placeholder={pleaseEnterTheLimitAmountYouWantTo}
                className={`vehicle-managemet-add-fleet-limit-input ${errorValue ? 'vehicle-managemet-add-fleet-limit-input-error' : ''
                  }`}
                onInput={(e) => (e.target.value = e.target.value.toUpperCase())}
                onChange={handleChange}
                maxLength={10}
                value={value}
                style={{
                  flex: '1 1 auto',
                  width: 340,
                  marginTop: 10,
                  marginBottom: 20,
                  borderColor: errorValue ? 'red' : '#94a3b8',
                }}
              />

              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  flexDirection: 'row',
                  justifyContent: 'center',
                  gap: 20,
                }}
              >
                <button
                  onClick={() => {
                    setValue('15.000');
                    if (errorValue) setErrorValue(false);
                  }}
                  className="vehicle-managemet-add-fleet-limit-badge"
                >
                  15.000 TL
                </button>
                <button
                  onClick={() => {
                    setValue('20.000');
                    if (errorValue) setErrorValue(false);
                  }}
                  className="vehicle-managemet-add-fleet-limit-badge"
                >
                  20.000 TL
                </button>
                <button
                  onClick={() => {
                    setValue('25.000');
                    if (errorValue) setErrorValue(false);
                  }}
                  className="vehicle-managemet-add-fleet-limit-badge"
                >
                  25.000 TL
                </button>
              </div>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  flexDirection: 'row',
                  justifyContent: 'center',
                  gap: 20,
                  marginTop: 20,
                }}
              >
                <button
                  onClick={() => {
                    setValue('30.000');
                    if (errorValue) setErrorValue(false);
                  }}
                  className="vehicle-managemet-add-fleet-limit-badge"
                >
                  30.000 TL
                </button>
                <button
                  onClick={() => {
                    setValue('35.000');
                    if (errorValue) setErrorValue(false);
                  }}
                  className="vehicle-managemet-add-fleet-limit-badge"
                >
                  35.000 TL
                </button>
              </div>
            </div>
            <div
              className="footer-content"
              style={{ marginTop: 20, display: 'flex', flexDirection: 'column' }}
            >
              <Button
                id="redirect"
                data-testid="redirect"
                onClick={confirmAddFleetLimit}
                className="btn-blue"
              >
                <span>
                  <Text t="approveAndProceedToPayment" />
                </span>
              </Button>
              <button
                style={{
                  borderWidth: 0,
                  backgroundColor: 'transparent',
                  textDecoration: 'underline',
                  marginTop: 10,
                }}
                data-testid="cancel-add-fleet-limit-id"
                onClick={handleFleetCancel}
              >
                <Text t="giveUp" />
              </button>
            </div>
          </div>
        </Modal>
      </div>
    </div>
  );
};
export default AddNewCarOrderSummary;
